import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { pharmacyApi, type BatchOption } from "@/api/pharmacy";
import { patientApi } from "@/api/patient";
import { doctorApi } from "@/api/doctor";
import { billingApi } from "@/api/billing";
import {
  DoctorSearchableSelect,
  EXTERNAL_DOCTOR_ID,
} from "@/components/common/DoctorSearchableSelect";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, UserPlus, Trash2, FileDown, Check } from "lucide-react";
import ViewBill from "@/components/billing/ViewBill";
interface PatientSummary {
  id: string;
  name: string;
  phone?: string;
  uhid?: string;
}

interface DrugSummary {
  id: string;
  name: string;
  strength?: string;
  form?: string;
  unit?: string;
}

interface DrugSearchResult extends DrugSummary {
  fifoBatch?: BatchOption | null;
}

interface SaleItem {
  id: string;
  drug?: DrugSummary;
  drugId: string;
  quantity: string;
  unitPrice?: number;
  discount?: string;
  fifoBatch?: BatchOption | null;
  stockAvailable?: number | null;
}

function cartQtyForDrug(items: SaleItem[], drugId: string, excludeItemId?: string): number {
  return items
    .filter((it) => it.drugId === drugId && it.id !== excludeItemId)
    .reduce((sum, it) => sum + (parseInt(it.quantity, 10) || 0), 0);
}

/** YYYY-MM-DD in local calendar for `<input type="date">`. */
function todayLocalISODate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function PharmacyWalkInSalePage() {
  const [patientSearch, setPatientSearch] = useState("");
  const [patientResults, setPatientResults] = useState<PatientSummary[]>([]);
  const [patientLoading, setPatientLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientSummary | null>(null);

  const [drugSearch, setDrugSearch] = useState("");
  const [drugResults, setDrugResults] = useState<DrugSearchResult[]>([]);
  const [drugLoading, setDrugLoading] = useState(false);

  const [items, setItems] = useState<SaleItem[]>([]);
  const [lastCreatedBill, setLastCreatedBill] = useState<{ id: string; billNumber: string } | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [quickRegisterOpen, setQuickRegisterOpen] = useState(false);
  const [billDate, setBillDate] = useState(todayLocalISODate);
  const [recommendedDoctorValue, setRecommendedDoctorValue] = useState("");
  const [quickRegisterForm, setQuickRegisterForm] = useState({
    name: "",
    phone: "",
    gender: "MALE" as "MALE" | "FEMALE" | "OTHER",
  });

  const queryClient = useQueryClient();

  const { data: doctors = [] } = useQuery({
    queryKey: ["hospital-doctors"],
    queryFn: () => doctorApi.getDoctorsByHospital(),
    staleTime: 300000,
  });

  const addPatientMutation = useMutation({
    mutationFn: async (payload: { name: string; phoneNumber: string; gender?: "MALE" | "FEMALE" | "OTHER" }) => {
      const name = payload.name.trim();
      const phoneNumber = payload.phoneNumber.trim();
      if (!name || !phoneNumber) {
        throw new Error("Name and phone are required");
      }
      const created = await patientApi.createPatient({
        name,
        phoneNumber,
        gender: payload.gender,
      });
      return created;
    },
    onSuccess: (patient: any) => {
      toast.success("Patient registered and synced");
      const summary: PatientSummary = {
        id: patient.id,
        name: patient.name,
        phone: patient.phone,
        uhid: patient.patientUniqueId ?? patient.uhid,
      };
      setSelectedPatient(summary);
      setPatientResults([]);
      setPatientSearch("");
      setQuickRegisterOpen(false);
      setQuickRegisterForm({ name: "", phone: "", gender: "MALE" });
      queryClient.invalidateQueries({ queryKey: ["hospital-patients"] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || "Failed to add patient";
      toast.error(msg);
    },
  });

  const createSaleMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPatient) throw new Error("Select or create a patient first");
      if (!recommendedDoctorValue) throw new Error("Select the recommending doctor");
      const cleanItems = items
        .map((it) => {
          const qty = parseInt(it.quantity, 10) || 0;
          const unit = it.unitPrice ?? 0;
          const pct = Math.min(100, Math.max(0, parseFloat(it.discount ?? "0") || 0));
          const discountAmount = Math.round((qty * unit * pct) / 100 * 100) / 100;
          return {
            drugId: it.drugId,
            quantity: qty,
            discountAmount,
            itemId: it.id,
          };
        })
        .filter((it) => it.drugId && it.quantity && it.quantity > 0);

      for (const it of cleanItems) {
        const row = items.find((x) => x.id === it.itemId);
        const stock = row?.stockAvailable;
        if (stock == null) continue;
        const totalForDrug = cleanItems
          .filter((x) => x.drugId === it.drugId)
          .reduce((s, x) => s + x.quantity, 0);
        if (totalForDrug > stock) {
          throw new Error(`Only ${stock} unit(s) available in stock for this medicine`);
        }
      }

      if (cleanItems.length === 0) throw new Error("Add at least one valid item");
      const externalDoctor = recommendedDoctorValue === EXTERNAL_DOCTOR_ID;
      const res = await pharmacyApi.walkInSale({
        patientId: selectedPatient.id,
        billDate,
        recommendedDoctorId: externalDoctor ? null : recommendedDoctorValue,
        externalDoctor,
        items: cleanItems.map(({ drugId, quantity, discountAmount }) => ({
          drugId,
          quantity,
          discountAmount,
        })),
      });
      const bill = (res.data as { data?: { id: string; billNumber?: string } })?.data;
      return bill;
    },
    onSuccess: (bill: { id: string; billNumber?: string } | undefined) => {
      if (bill?.id) {
        setLastCreatedBill({ id: bill.id, billNumber: bill.billNumber ?? bill.id });
        setBillDialogOpen(true);
      }
      toast.success("Walk-in sale created. Bill generated.");
      setItems([]);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || "Failed to create sale";
      toast.error(msg);
    },
  });

  const handleDownloadBillPdf = async () => {
    if (!lastCreatedBill) return;
    setDownloadingPdf(true);
    try {
      const response = await billingApi.exportBillPDF(lastCreatedBill.id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Bill-${lastCreatedBill.billNumber || lastCreatedBill.id}.pdf`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success("Bill PDF downloaded");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to download bill PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const runPatientSearch = async (term: string) => {
    if (!term.trim()) return;
    setPatientLoading(true);
    try {
      let results: any[] = [];
      if (term.length === 10 && !isNaN(Number(term))) {
        results = await patientApi.getPatientByPhone(term);
      } else {
        results = await patientApi.getPatientByName(term);
      }
      setPatientResults(results || []);
      if (results && results.length === 1) {
        const p = results[0];
        const picked = {
          id: p.id,
          name: p.name,
          phone: p.phone,
          uhid: p.patientUniqueId ?? p.uhid,
        } as PatientSummary;
        setSelectedPatient(picked);
        setPatientResults([]);
        setPatientSearch("");
      } else {
        setSelectedPatient(null);
      }
    } catch (err: any) {
      toast.error((err as any)?.response?.data?.message || "Failed to search patients");
      setPatientResults([]);
      setSelectedPatient(null);
    } finally {
      setPatientLoading(false);
    }
  };

  const runDrugSearch = async (term: string) => {
    if (!term.trim()) return;
    setDrugLoading(true);
    try {
      const res = await api.get<{ data?: DrugSummary[] }>("/api/prescription/drugs", {
        params: { search: term },
      });
      const list = res.data?.data ?? [];
      const enriched = await Promise.all(
        list.slice(0, 30).map(async (drug): Promise<DrugSearchResult> => {
          try {
            const batchRes = await pharmacyApi.getBatchesForDrug(drug.id, 1);
            const fifoBatch = batchRes.data?.data?.[0] ?? null;
            return { ...drug, fifoBatch };
          } catch {
            return { ...drug, fifoBatch: null };
          }
        }),
      );
      setDrugResults(enriched);
    } catch (err: any) {
      toast.error((err as any)?.response?.data?.message || "Failed to search drugs");
      setDrugResults([]);
    } finally {
      setDrugLoading(false);
    }
  };

  // Search as you type: patient (debounced)
  useEffect(() => {
    const term = patientSearch.trim();
    if (term.length < 2) {
      setPatientResults([]);
      return;
    }
    const id = setTimeout(() => runPatientSearch(term), 300);
    return () => clearTimeout(id);
  }, [patientSearch]);

  // Search as you type: drug (debounced)
  useEffect(() => {
    const term = drugSearch.trim();
    if (term.length < 2) {
      setDrugResults([]);
      return;
    }
    const id = setTimeout(() => runDrugSearch(term), 300);
    return () => clearTimeout(id);
  }, [drugSearch]);

  const addItemFromDrug = async (drug: DrugSearchResult) => {
    let unitPrice = 0;
    let fifoBatch: BatchOption | null = drug.fifoBatch ?? null;
    let stockAvailable: number | null = null;
    try {
      const stockRes = await pharmacyApi.getStock(drug.id);
      stockAvailable = Number(stockRes.data?.data?.quantity ?? 0);
    } catch {
      stockAvailable = null;
    }
    if (fifoBatch?.mrp != null) {
      unitPrice = fifoBatch.mrp;
    } else {
      try {
        const res = await pharmacyApi.getBatchesForDrug(drug.id, 1);
        const batches = res.data?.data ?? [];
        if (batches.length > 0) {
          fifoBatch = batches[0] ?? null;
          if (batches[0].mrp != null) unitPrice = batches[0].mrp;
        }
      } catch {
        // use 0 if batch fetch fails
      }
    }
    setItems((prev) => [
      ...prev,
      {
        id: `${drug.id}-${Date.now()}`,
        drug,
        drugId: drug.id,
        quantity: "1",
        unitPrice,
        discount: "0",
        fifoBatch,
        stockAvailable,
      },
    ]);
    setDrugSearch("");
    setDrugResults([]);
  };

  const updateItemQuantity = (id: string, value: string) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, quantity: value } : it)));
  };

  const updateItemDiscount = (id: string, value: string) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, discount: value } : it)));
  };

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const stockIssues = useMemo(() => {
    const byDrug = new Map<string, { stock: number; requested: number }>();
    for (const it of items) {
      const qty = parseInt(it.quantity, 10) || 0;
      if (!it.drugId || qty <= 0) continue;
      const prev = byDrug.get(it.drugId) ?? { stock: it.stockAvailable ?? 0, requested: 0 };
      prev.requested += qty;
      if (it.stockAvailable != null) prev.stock = it.stockAvailable;
      byDrug.set(it.drugId, prev);
    }
    return [...byDrug.entries()].filter(([, v]) => v.stock >= 0 && v.requested > v.stock);
  }, [items]);

  const hasStockBlock = stockIssues.length > 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Walk-in Sale / Outside Prescription</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Patient selection / registration */}
          <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
            <h3 className="font-medium text-sm">1. Select or register patient</h3>
            <div className="flex flex-col md:flex-row gap-2 items-center">
              <Popover open={patientResults.length > 0} modal={false}>
                <PopoverAnchor asChild>
                  <div className="relative w-full md:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or phone..."
                      value={patientSearch}
                      onChange={(e) => setPatientSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </PopoverAnchor>
                <PopoverContent
                  className="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <div className="max-h-48 overflow-y-auto">
                    {patientResults.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={`flex w-full items-start justify-between gap-3 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted ${
                          selectedPatient?.id === p.id ? "bg-blue-50" : ""
                        }`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setSelectedPatient(p);
                          setPatientResults([]);
                          setPatientSearch("");
                        }}
                      >
                        <span className="font-medium">{p.name}</span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {[p.phone, p.uhid].filter(Boolean).join(" · ")}
                        </span>
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Button type="button" variant="outline" onClick={() => patientSearch.trim() && runPatientSearch(patientSearch)} disabled={patientLoading}>
                {patientLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
              </Button>
              <span className="text-xs text-muted-foreground">or</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const term = patientSearch.trim();
                  const isPhone = term.length === 10 && !isNaN(Number(term));
                  setQuickRegisterForm((f) => ({
                    ...f,
                    name: isPhone ? "" : term,
                    phone: isPhone ? term : "",
                  }));
                  setQuickRegisterOpen(true);
                }}
              >
                <UserPlus className="h-4 w-4 mr-1" />
                Quick register
              </Button>
            </div>
            {selectedPatient && (
              <p className="text-xs text-muted-foreground mt-1">
                Selected patient: <span className="font-medium">{selectedPatient.name}</span>{" "}
                {selectedPatient.uhid && <>({selectedPatient.uhid})</>}
              </p>
            )}
          </div>

          {/* Recommending doctor */}
          <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
            <h3 className="font-medium text-sm">2. Recommending doctor</h3>
            <div className="w-full md:max-w-md">
              <Label htmlFor="walkin-recommended-doctor">Doctor who recommended the medicines</Label>
              <DoctorSearchableSelect
                id="walkin-recommended-doctor"
                doctors={doctors}
                value={recommendedDoctorValue}
                onValueChange={setRecommendedDoctorValue}
                allowExternalDoctor
                placeholder="Search and select doctor…"
                className="mt-1"
              />
            </div>
          </div>

          {/* Drug selection */}
          <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
            <h3 className="font-medium text-sm">3. Add medicines (from outside prescription)</h3>
            <div className="flex flex-col md:flex-row gap-2 items-center">
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search drug by name or strength..."
                  value={drugSearch}
                  onChange={(e) => setDrugSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button type="button" variant="outline" onClick={() => drugSearch.trim() && runDrugSearch(drugSearch)} disabled={drugLoading}>
                {drugLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search drug"}
              </Button>
            </div>
            {drugResults.length > 0 && (
              <div className="mt-2 max-h-48 overflow-auto border rounded-md bg-white">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Medicine</TableHead>
                      <TableHead className="w-24">Type</TableHead>
                      <TableHead className="w-28">Batch</TableHead>
                      <TableHead className="w-28">Expiry</TableHead>
                      <TableHead className="w-24 text-right">MRP (₹)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drugResults.map((d) => (
                      <TableRow
                        key={d.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => addItemFromDrug(d)}
                      >
                        <TableCell className="font-medium">
                          {d.name} {d.strength && `(${d.strength})`}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{d.form ?? "—"}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {d.fifoBatch?.batchNo ?? <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-xs">
                          {d.fifoBatch?.expiryDate
                            ? String(d.fifoBatch.expiryDate).slice(0, 10)
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-xs">
                          {d.fifoBatch?.mrp != null && d.fifoBatch.mrp > 0
                            ? d.fifoBatch.mrp.toFixed(2)
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {items.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-muted-foreground">Medicines added ({items.length})</h4>
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8">#</TableHead>
                        <TableHead>Drug</TableHead>
                        <TableHead className="w-36">Batch</TableHead>
                        <TableHead className="w-28">Expiry</TableHead>
                        <TableHead className="w-24 text-right">In stock</TableHead>
                        <TableHead className="w-24">Qty</TableHead>
                        <TableHead className="w-28">Unit price (₹)</TableHead>
                        <TableHead className="w-28">Discount (%)</TableHead>
                        <TableHead className="w-28">Line total (₹)</TableHead>
                        <TableHead className="w-10" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((it, idx) => {
                        const qty = parseInt(it.quantity, 10) || 0;
                        const unit = it.unitPrice ?? 0;
                        const pct = Math.min(100, Math.max(0, parseFloat(it.discount ?? "0") || 0));
                        const lineTotal = Math.round(qty * unit * (1 - pct / 100) * 100) / 100;
                        const otherCartQty = cartQtyForDrug(items, it.drugId, it.id);
                        const stockTotal = it.stockAvailable ?? null;
                        const remainingForLine =
                          stockTotal != null ? Math.max(0, stockTotal - otherCartQty) : null;
                        const overStock = remainingForLine != null && qty > remainingForLine;
                        return (
                          <TableRow key={it.id}>
                            <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                            <TableCell className="font-medium">
                              {it.drug
                                ? `${it.drug.name} ${it.drug.strength || ""} ${it.drug.form || ""}`.trim()
                                : it.drugId}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {it.fifoBatch?.batchNo ? it.fifoBatch.batchNo : <span className="text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell className="text-xs">
                              {it.fifoBatch?.expiryDate
                                ? String(it.fifoBatch.expiryDate).slice(0, 10)
                                : <span className="text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell className={`text-right text-xs tabular-nums ${overStock ? "text-rose-600 font-semibold" : ""}`}>
                              {stockTotal != null ? (
                                <>
                                  {remainingForLine}
                                  {otherCartQty > 0 && (
                                    <span className="block text-[10px] text-muted-foreground font-normal">
                                      of {stockTotal} total
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min={1}
                                step={1}
                                className={`h-8 w-20 ${overStock ? "border-rose-400" : ""}`}
                                value={it.quantity}
                                onChange={(e) => updateItemQuantity(it.id, e.target.value)}
                              />
                              {overStock && remainingForLine != null && (
                                <p className="text-[10px] text-rose-600 mt-0.5">
                                  Max {remainingForLine} available
                                </p>
                              )}
                            </TableCell>
                            <TableCell className="tabular-nums">
                              {(it.unitPrice ?? 0) > 0 ? `₹${(it.unitPrice ?? 0).toFixed(2)}` : "—"}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                className="h-8 w-20"
                                value={it.discount ?? "0"}
                                onChange={(e) => updateItemDiscount(it.id, e.target.value)}
                                placeholder="0"
                              />
                            </TableCell>
                            <TableCell className="font-medium tabular-nums">
                              ₹{lineTotal.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-destructive"
                                onClick={() => removeItem(it.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex justify-end border-t pt-2">
                  <p className="text-sm font-medium">
                    Estimated total: ₹
                    {items
                      .reduce((sum, it) => {
                        const qty = parseInt(it.quantity, 10) || 0;
                        const unit = it.unitPrice ?? 0;
                        const pct = Math.min(100, Math.max(0, parseFloat(it.discount ?? "0") || 0));
                        return sum + Math.round(qty * unit * (1 - pct / 100) * 100) / 100;
                      }, 0)
                      .toFixed(2)}
                    <span className="text-muted-foreground font-normal ml-1">(excl. GST; actual on bill)</span>
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Bill date */}
          <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
            <h3 className="font-medium text-sm">4. Bill date</h3>
            <div className="flex flex-col md:flex-row gap-3 md:items-end">
              <div className="w-full md:w-64">
                <Label htmlFor="walkin-bill-date">Bill date</Label>
                <Input
                  id="walkin-bill-date"
                  type="date"
                  value={billDate}
                  max={todayLocalISODate()}
                  onChange={(e) => setBillDate(e.target.value)}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Default is today. You can select a back date if needed.
              </p>
            </div>
          </div>

          {/* Submit */}
          <div className="flex flex-col items-end gap-2">
            {hasStockBlock && (
              <p className="text-sm text-rose-600">
                Quantity exceeds available stock for one or more medicines. Reduce qty to match inventory.
              </p>
            )}
            <Button
              type="button"
              onClick={() => createSaleMutation.mutate()}
              disabled={
                createSaleMutation.isPending ||
                !selectedPatient ||
                !recommendedDoctorValue ||
                items.length === 0 ||
                hasStockBlock
              }
            >
              {createSaleMutation.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating bill...
                </span>
              ) : (
                "Create bill & dispense"
              )}
            </Button>
          </div>

          {/* Post-sale: bill created — Download PDF (OPD-style with hospital name, GST, address) */}
          {lastCreatedBill && (
            <div className="mt-4 p-4 rounded-lg border border-green-200 bg-green-50 space-y-3">
              <p className="font-medium text-green-800">
                Bill <span className="font-mono">{lastCreatedBill.billNumber}</span> created successfully.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleDownloadBillPdf}
                  disabled={downloadingPdf}
                >
                  <span className="inline-flex items-center gap-2">
                    {downloadingPdf ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileDown className="h-4 w-4" />
                    )}
                    Download bill PDF
                  </span>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setBillDialogOpen(true)}
                >
                  Record payment / view bill
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setLastCreatedBill(null)}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Done
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick register dialog — same patient API as main registration, so new patients stay in sync */}
      <Dialog open={quickRegisterOpen} onOpenChange={setQuickRegisterOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Quick register patient</DialogTitle>
            <DialogDescription>
              Enter name and phone. This patient will be added to the hospital&apos;s patient list.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="qr-name">Name *</Label>
              <Input
                id="qr-name"
                value={quickRegisterForm.name}
                onChange={(e) => setQuickRegisterForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Full name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="qr-phone">Phone (10 digits) *</Label>
              <Input
                id="qr-phone"
                type="tel"
                maxLength={10}
                value={quickRegisterForm.phone}
                onChange={(e) => setQuickRegisterForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "") }))}
                placeholder="10-digit mobile"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="qr-gender">Gender</Label>
              <select
                id="qr-gender"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                value={quickRegisterForm.gender}
                onChange={(e) =>
                  setQuickRegisterForm((f) => ({ ...f, gender: e.target.value as "MALE" | "FEMALE" | "OTHER" }))
                }
              >
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setQuickRegisterOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                const { name, phone, gender } = quickRegisterForm;
                if (!name.trim()) {
                  toast.error("Name is required");
                  return;
                }
                if (!phone.trim() || phone.length !== 10) {
                  toast.error("Valid 10-digit phone is required");
                  return;
                }
                addPatientMutation.mutate({
                  name: name.trim(),
                  phoneNumber: phone.trim(),
                  gender,
                });
              }}
              disabled={addPatientMutation.isPending}
            >
              {addPatientMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Register"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {lastCreatedBill && (
        <ViewBill
          billId={lastCreatedBill.id}
          isOpen={billDialogOpen}
          onClose={() => setBillDialogOpen(false)}
          onSuccess={() => {
            // When payment is recorded, refocus the success banner
          }}
        />
      )}
    </div>
  );
}
